import { useState } from 'react'
import { useLocalCVList } from '../../hooks/useLocalCVList'
import { useCV } from '../../hooks/useCV'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from '../../contexts/LanguageContext'
import { AuthModal } from '../auth/AuthModal'
import { CVListCompact } from '../cv-list'
import type { CV } from '../../types/cv'

interface CVListPanelProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
  onImportClick: () => void
  onLastCVDeleted?: () => void
  hideHeader?: boolean // For mobile drawer where header is provided externally
}

const emptyCV: CV = {
  personalInfo: { fullName: '', title: '', email: '', phone: '', location: '', links: [] },
  summary: '',
  experience: [],
  education: [],
  skills: { technical: [], soft: [], tools: [] },
  languages: [],
  certifications: [],
  projects: []
}

export function CVListPanel({ isCollapsed, onToggleCollapse, onImportClick, onLastCVDeleted, hideHeader = false }: CVListPanelProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { cvs, addCV, updateCV, deleteCV } = useLocalCVList()
  const { activeCVId, loadCV } = useCV()
  const [showAuthModal, setShowAuthModal] = useState(false)

  const handleSelectCV = (cvId: string) => {
    const selectedCV = cvs.find((cv) => cv.id === cvId)
    if (selectedCV) {
      loadCV(cvId, selectedCV.cv)
    }
  }

  const handleCreateNew = () => {
    if (!user && cvs.length >= 1) {
      setShowAuthModal(true)
      return
    }
    const newId = addCV(emptyCV, t.cvList.newCVDefault, 'clean')
    loadCV(newId, emptyCV)
  }

  const handleImportWithCheck = () => {
    if (!user && cvs.length >= 1) {
      setShowAuthModal(true)
      return
    }
    onImportClick()
  }

  const handleDeleteCV = (cvId: string) => {
    deleteCV(cvId)
    // If deleted the active CV, load another or show welcome screen
    if (cvId === activeCVId) {
      const remaining = cvs.filter(cv => cv.id !== cvId)
      if (remaining.length > 0) {
        loadCV(remaining[0].id, remaining[0].cv)
      } else {
        onLastCVDeleted?.()
      }
    }
  }

  const handleRenameCV = (cvId: string, newTitle: string) => {
    updateCV(cvId, { title: newTitle })
  }

  if (isCollapsed) {
    return (
      <>
        <div className="w-14 bg-white border-r border-gray-200 flex flex-col items-center py-4 gap-3">
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
            title={t.cvList.expand}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <div className="w-8 h-px bg-gray-200" />

          <button
            onClick={handleCreateNew}
            className="p-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
            title={t.cvList.newCV}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>

          <button
            onClick={handleImportWithCheck}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
            title={t.cvList.importBtn}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </button>

          <div className="flex-1" />

          {/* CV count badge */}
          {cvs.length > 0 && (
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
              {cvs.length}
            </div>
          )}
        </div>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialView="register"
          title={t.cvLimit.title}
          description={t.cvLimit.description}
        />
      </>
    )
  }

  return (
    <div className={`${hideHeader ? 'w-full' : 'w-full lg:w-72'} bg-gray-50 lg:border-r border-gray-200 flex flex-col h-full min-h-0`}>
      {/* Header */}
      <div className={`p-4 border-b border-gray-200 bg-white ${hideHeader ? 'pt-2' : ''}`}>
        {/* Title and collapse */}
        {!hideHeader && (
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">{t.cvList.title}</h2>
            {/* Collapse button - only visible on desktop */}
            <button
              onClick={onToggleCollapse}
              className="hidden lg:block p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              title={t.cvList.collapse}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Action buttons - always visible */}
        <div className="flex gap-2">
          <button
            onClick={handleCreateNew}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors min-h-[44px]"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            {t.cvList.new}
          </button>
          <button
            onClick={handleImportWithCheck}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            {t.cvList.import}
          </button>
        </div>
      </div>

      {/* CV List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {cvs.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-sm text-gray-500">{t.cvList.empty}</p>
            <p className="text-xs text-gray-400 mt-1">
              {t.cvList.emptySubtitle}
            </p>
          </div>
        ) : (
          cvs.map((cv) => (
            <CVListCompact
              key={cv.id}
              cv={cv}
              isActive={cv.id === activeCVId}
              onSelect={() => handleSelectCV(cv.id)}
              onDelete={() => handleDeleteCV(cv.id)}
              onRename={(newTitle) => handleRenameCV(cv.id, newTitle)}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {cvs.length > 0 && (
        <div className="p-3 border-t border-gray-200 bg-white">
          <p className="text-xs text-gray-500 text-center">
            {cvs.length} {cvs.length === 1 ? t.cvList.saved : t.cvList.savedPlural}
          </p>
        </div>
      )}

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialView="register"
        title={t.cvLimit.title}
        description={t.cvLimit.description}
      />
    </div>
  )
}
